import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import SmoothScroll from "@/components/SmoothScroll";
import Landing from "@/pages/Landing";
import { Login, Register } from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import Dashboard from "@/pages/Dashboard";
import ServiceDetail from "@/pages/ServiceDetail";
import Reports from "@/pages/Reports";
import SubscribeGate from "@/pages/SubscribeGate";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F2F2]">
        <div className="font-mono-aq text-xs tracking-[0.3em] uppercase text-[#B6B09F]">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Subscribed({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F2F2]">
        <div className="font-mono-aq text-xs tracking-[0.3em] uppercase text-[#B6B09F]">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const plan = user.plan || "free";
  const isSubscribed = user.role === "admin" || ["starter", "pro", "enterprise"].includes(plan);
  if (!isSubscribed) return <SubscribeGate />;
  return children;
}

function GuestOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Detect OAuth callback synchronously during render (playbook — prevents race conditions)
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/register" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/forgot-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/dashboard/services/:key" element={<Protected><Subscribed><ServiceDetail /></Subscribed></Protected>} />
      <Route path="/dashboard/reports" element={<Protected><Subscribed><Reports /></Subscribed></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <SmoothScroll />
          <Toaster position="bottom-right" toastOptions={{ style: { background: "#0A0A0A", color: "#EAE4D5", border: "1px solid #B6B09F" } }} />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}
